import React from "react";
import { Button as RawButton } from "@/components/ui/button";
import { Clock, Car, ArrowRight, Users } from "lucide-react";
import { motion } from "framer-motion";

/**
 * @param {{
 *   vehicle: {
 *     id: string;
 *     provider: string;
 *     vehicle_type: string;
 *     pickup_location: string;
 *     dropoff_location: string;
 *     price: number;
 *     duration_minutes: number;
 *     max_passengers: number;
 *     image: string;
 *     amenities: string[];
 *   };
 *   onSelect: () => void;
 *   index: number;
 * }} props
 */
export default function TransportCard({ vehicle, onSelect, index }) {
  const Button = /** @type {React.ComponentType<any>} */ (RawButton);

  const formattedDuration = `${Math.floor(vehicle.duration_minutes / 60)}h ${vehicle.duration_minutes % 60}m`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      onClick={onSelect}
      className="rounded-xl overflow-hidden shadow-md bg-white flex flex-col cursor-pointer hover:shadow-xl transition-shadow"
    >
      {/* Image */}
      <div className="relative h-44 shrink-0 bg-gray-100">
        <img
          src={vehicle.image}
          alt={`${vehicle.provider} ${vehicle.vehicle_type}`}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

        {/* MPV Badge */}
        <span className="absolute top-3 left-3 bg-[#0B2421] text-[#D4AF37] text-xs font-bold px-2.5 py-1 rounded-md">
          {vehicle.vehicle_type}
        </span>

        {/* Provider at bottom-left */}
        <div className="absolute bottom-3 left-3 flex items-center gap-1.5 text-white">
          <Car className="h-4 w-4" />
          <span className="text-sm font-medium">{vehicle.provider}</span>
        </div>

        {/* Price at bottom-right */}
        <div className="absolute bottom-3 right-3 text-right">
          <p className="text-white/70 text-[10px] font-medium">From</p>
          <p className="text-white font-bold text-lg leading-none">
            SGD {vehicle.price}<span className="text-white/70 text-xs font-normal">/trip</span>
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col gap-2.5 flex-1">
        {/* Route */}
        <div className="flex items-center gap-2 text-sm font-semibold text-[#0B2421]">
          <span className="truncate">{vehicle.pickup_location}</span>
          <ArrowRight className="h-4 w-4 text-[#D4AF37] flex-shrink-0" />
          <span className="truncate text-right">{vehicle.dropoff_location}</span>
        </div>

        {/* Duration + Passengers */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5 text-[#0B2421]" />
            <span>{formattedDuration}</span>
          </div>
          <div className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5 text-[#0B2421]" />
            <span>Max {vehicle.max_passengers}</span>
          </div>
        </div>

        {/* Book button */}
        <button
          onClick={(e) => { e.stopPropagation(); onSelect(); }}
          className="mt-auto w-full bg-[#0B2421] hover:bg-[#0B2421]/85 text-white text-sm font-semibold py-2.5 rounded-lg flex items-center justify-center gap-1.5 transition-colors"
        >
          Select
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </motion.div>
  );
}
